import React from 'react';
import { QTY_LABEL } from '../data/Localization';

/**
 * QuantityPicker component for selecting and adjusting quantity.
 * @module QuantityPicker
 * @param {Object} props - The component props.
 * @param {string} props.error - The error message, if any.
 * @param {Function} props.onRemoveError - The function to remove the error message.
 * @param {Function} props.onDecrement - The function to decrement the quantity.
 * @param {Function} props.onChange - The function to handle quantity change.
 * @param {number} props.qty - The current quantity value.
 * @returns {JSX.Element} The QuantityPicker component.
 */
const QuantityPicker = ({ error, onRemoveError, ...props }) => {
	const handleClickOutsideRef = React.useRef(null);
	const errorRef = React.useRef(null);

	React.useEffect(() => {
		// Handle click outside of error message
		if (error) {
			handleClickOutsideRef.current = (e) => {
				if (!errorRef.current || errorRef.current.contains(e.target)) {
					return;
				}
				onRemoveError();
			};

			document.addEventListener('click', handleClickOutsideRef.current);
		} else {
			document.removeEventListener('click', handleClickOutsideRef.current);
		}
	}, [error, onRemoveError]);

	return (
		<div className="prc__pricing-form">
			<span className="prc__pricing-text">{QTY_LABEL}</span>
			<button className="prc__less-qty-btn" data-target="true" aria-label={Granite.I18n.get('Decrease quantity')} onClick={props.onDecrement}>
				<span className="prc__minus">−</span>
			</button>
			<input className="prc__qty" onChange={props.onChange} value={props.qty} type="number" aria-label={Granite.I18n.get('Quantity')} />
			<div ref={errorRef} className={`prc__error-message input-error-message ${error ? 'is-active' : 'hide'}`}>
				{error}
				<calcite-button
					data-click="handleRemoveErrorMsg"
					data-testid="remove-error-btn"
					onClick={onRemoveError}
					icon-end="x"
					round=""
					scale="s"
					kind="neutral"
					appearance="transparent"></calcite-button>
			</div>
			<button className="prc__plus-qty-btn" data-target="true" aria-label={Granite.I18n.get('Increase quantity')} onClick={props.onIncrement}>
				<span className="prc__plus">+</span>
			</button>
		</div>
	);
};

export default QuantityPicker;
